import { ref, computed } from 'vue'

interface User {
  email: string
  name: string
  picture?: string
}

const user = ref<User | null>(null)
const isLoading = ref(false)

const ADMIN_EMAILS = ['henry@saskatoonauto.ca']
const ADMIN_PASSWORD = 'SAC2025Admin!'

export function useAuth() {
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value ? ADMIN_EMAILS.includes(user.value.email) : false)

  const initGoogleAuth = async () => {
    return new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.onload = () => resolve(true)
      document.head.appendChild(script)
    })
  }

  const passwordLogin = async (password: string) => {
    isLoading.value = true
    try {
      if (password === ADMIN_PASSWORD) {
        user.value = {
          email: 'henry@saskatoonauto.ca',
          name: 'Administrator',
          picture: undefined
        }
        localStorage.setItem('admin_user', JSON.stringify(user.value))
      } else {
        throw new Error('Invalid password')
      }
    } catch (error) {
      console.error('Login error:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const signIn = async () => {
    isLoading.value = true
    try {
      await initGoogleAuth()
      
      // Initialize Google Sign-In
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse
      })

      // Show the sign-in prompt
      window.google.accounts.id.prompt()
    } catch (error) {
      console.error('Sign-in error:', error)
    } finally {
      isLoading.value = false
    }
  }

  const handleCredentialResponse = (response: any) => {
    try {
      const decoded = JSON.parse(atob(response.credential.split('.')[1]))
      
      if (ADMIN_EMAILS.includes(decoded.email)) {
        user.value = {
          email: decoded.email,
          name: decoded.name,
          picture: decoded.picture
        }
        localStorage.setItem('admin_user', JSON.stringify(user.value))
      } else {
        alert('Access denied. Only authorized administrators can access this dashboard.')
      }
    } catch (error) {
      console.error('Error processing authentication:', error)
    }
  }

  const signOut = () => {
    user.value = null
    localStorage.removeItem('admin_user')
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect()
    }
  }

  const checkAuthStatus = () => {
    const savedUser = localStorage.getItem('admin_user')
    if (savedUser) {
      try {
        user.value = JSON.parse(savedUser)
      } catch (error) {
        localStorage.removeItem('admin_user')
      }
    }
  }

  return {
    user: computed(() => user.value),
    isAuthenticated,
    isAdmin,
    isLoading: computed(() => isLoading.value),
    signIn,
    passwordLogin,
    signOut,
    checkAuthStatus
  }
}